/* =============================================================================
 * accessibility.js — BlackFin Rods (Accessibility Kit)
 * Library-agnostic runtime a11y enhancements. Loaded deferred, AFTER theme.js
 * so Flickity and the theme's own init have run.
 *
 *   1. Carousel pause/play control + prefers-reduced-motion  (WCAG 2.2.2, 2.3.3)
 *   2. Hidden-subtree tabindex sync (no focusables inside aria-hidden)  (4.1.2)
 *   3. Filter-accordion button semantics + keyboard  (2.1.1, 4.1.2)
 *
 * Self-contained, no dependencies, safe to drop onto any Flickity/Shopify theme.
 * ========================================================================== */
(function () {
  "use strict";

  var REDUCE = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* ---------------------------------------------------------------------------
   * 1. Carousel pause/play  (WCAG 2.2.2 Pause/Stop/Hide, 2.3.3 animation)
   * Auto-advancing carousels (announcement bar, logo bar, quotes, …) need a
   * keyboard-reachable pause control, and must honor reduced-motion.
   * ------------------------------------------------------------------------ */
  function hasAutoplay(el) {
    var raw = el.getAttribute("data-flickity-config") || el.getAttribute("data-flickity_options");
    if (!raw) return false;
    try {
      var cfg = JSON.parse(raw);
      return !!cfg.autoPlay; // truthy = number of ms
    } catch (e) {
      return /"autoPlay"\s*:\s*(?!false|0)\d/.test(raw); // tolerant fallback
    }
  }

  function enhanceCarousels(attempt) {
    if (typeof window.Flickity === "undefined") {
      if ((attempt || 0) < 20) return setTimeout(function () { enhanceCarousels((attempt || 0) + 1); }, 250);
      return; // Flickity never loaded — nothing to do
    }
    var sliders = document.querySelectorAll("[data-flickity-config], [data-flickity_options]");
    Array.prototype.forEach.call(sliders, function (el) {
      if (!hasAutoplay(el) || el.hasAttribute("data-a11y-pause-added")) return;
      var flkty = window.Flickity.data(el);
      if (!flkty) return; // not initialised yet; a later attempt will catch it
      el.setAttribute("data-a11y-pause-added", "true");

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "a11y-carousel-pause";
      var paused = false;

      function render() {
        btn.setAttribute("aria-pressed", paused ? "true" : "false");
        btn.setAttribute("aria-label", paused ? "Play automatic slideshow" : "Pause automatic slideshow");
        btn.innerHTML = paused
          ? '<span aria-hidden="true">▶</span>'
          : '<span aria-hidden="true">❚❚</span>';
      }
      function pause() { paused = true; try { flkty.pausePlayer(); } catch (e) {} render(); }
      function play() { paused = false; try { flkty.unpausePlayer(); } catch (e) {} render(); }

      btn.addEventListener("click", function () { paused ? play() : pause(); });

      // Reduced motion: stop autoplay outright and show the control as paused.
      if (REDUCE) { try { flkty.stopPlayer(); } catch (e) {} paused = true; }
      render();

      // Place the control inside the carousel's section so it inherits context.
      var host = el.closest("section, .AnnouncementBar, [data-section-id]") || el.parentNode;
      host.classList.add("a11y-has-carousel-pause");
      host.appendChild(btn);
    });
  }

  /* ---------------------------------------------------------------------------
   * 2. Hidden-subtree tabindex sync  (WCAG 4.1.2 / 2.1.1)
   * Anything inside an aria-hidden="true" subtree must not be in the tab order.
   * Generalises the Phase 1 announcement-bar fix; scoped to the header here.
   * ------------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
  function syncHidden(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll(FOCUSABLE), function (el) {
      if (el.classList.contains("a11y-carousel-pause")) return;
      var inHidden = el.closest('[aria-hidden="true"]');
      if (inHidden && scope.contains(inHidden)) {
        if (!el.hasAttribute("data-a11y-ti")) {
          el.setAttribute("data-a11y-ti", el.getAttribute("tabindex") === null ? "" : el.getAttribute("tabindex"));
        }
        el.setAttribute("tabindex", "-1");
      } else if (el.hasAttribute("data-a11y-ti")) {
        var prev = el.getAttribute("data-a11y-ti");
        if (prev === "") el.removeAttribute("tabindex"); else el.setAttribute("tabindex", prev);
        el.removeAttribute("data-a11y-ti");
      }
    });
  }
  function watchHidden(scope) {
    if (!scope) return;
    var obs = new MutationObserver(function () { syncHidden(scope); });
    obs.observe(scope, { subtree: true, childList: true, attributes: true, attributeFilter: ["aria-hidden"] });
    syncHidden(scope);
  }
  // Flickity carousels (product galleries, logo/quote/featured/look sliders) set
  // aria-hidden="true" on off-screen cells but leave the inner links focusable.
  // Watch each slider so those links drop out of the tab order on every page, not
  // just the header (fixes aria-hidden-focus on lookbook / prize-wheel / PDP gallery).
  function watchCarousels() {
    Array.prototype.forEach.call(
      document.querySelectorAll("[data-flickity-config], [data-flickity_options]"),
      function (el) {
        if (el.hasAttribute("data-a11y-hidden-watched")) return;
        el.setAttribute("data-a11y-hidden-watched", "true");
        watchHidden(el);
      }
    );
  }

  /* ---------------------------------------------------------------------------
   * 3. Filter accordion button semantics  (WCAG 2.1.1, 4.1.2)
   * The collection/search sidebar section titles are <span>s toggled by click.
   * Make them operable + announced without rewriting the theme's toggle JS:
   * add role/tabindex/aria-expanded, keep aria-expanded in sync with .active,
   * and route Enter/Space to a click.
   * ------------------------------------------------------------------------ */
  function enhanceAccordions() {
    var toggles = document.querySelectorAll(".category-filters-section-title");
    Array.prototype.forEach.call(toggles, function (t) {
      if (t.hasAttribute("data-a11y-acc")) return;
      t.setAttribute("data-a11y-acc", "true");
      t.setAttribute("role", "button");
      if (!t.hasAttribute("tabindex")) t.setAttribute("tabindex", "0");
      var setState = function () { t.setAttribute("aria-expanded", t.classList.contains("active") ? "true" : "false"); };
      setState();
      new MutationObserver(setState).observe(t, { attributes: true, attributeFilter: ["class"] });
      t.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          t.click();
        }
      });
    });
  }

  /* ---------------------------------------------------------------------------
   * 4. Decorative-image fallback  (WCAG 1.1.1)
   * Some JS-injected images (personalization / product-option swatches) render
   * with no alt attribute, so screen readers announce the file name. Mark any
   * image that is missing alt as decorative (alt="") so it is skipped.
   * ------------------------------------------------------------------------ */
  function altFallback() {
    Array.prototype.forEach.call(document.querySelectorAll("img:not([alt])"), function (img) {
      img.setAttribute("alt", "");
    });
  }

  /* ---------------------------------------------------------------------------
   * 5. Form-control label fallback  (WCAG 1.3.1, 4.1.2)
   * App-injected controls (personalization "validator", recommendation widgets)
   * and a couple of native theme controls (variant <select>, newsletter email)
   * render with no programmatic name. Give any unlabeled control an accessible
   * name derived from its context so screen readers don't fall back to nothing.
   * ------------------------------------------------------------------------ */
  function humanize(s) {
    return (s || "")
      .replace(/^contact\[|\]$/g, "")     // contact[email] -> email
      .replace(/[_\-\[\]]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();
  }
  function nameFor(el) {
    var type = (el.getAttribute("type") || el.tagName).toLowerCase();
    if (type === "email" || /email/i.test(el.name || el.id || "")) return "Email address";
    if (type === "search") return "Search";
    if (type === "tel") return "Phone number";
    if (el.tagName === "SELECT" && (el.name === "id" || /productselect/i.test(el.id))) return "Product variant";
    if (type === "submit" || type === "button") return el.value || "Submit";
    var label = humanize(el.getAttribute("placeholder") || el.name || el.id);
    return label || "Form field";
  }
  function labelFallback() {
    var controls = document.querySelectorAll("input:not([type=hidden]), select, textarea");
    Array.prototype.forEach.call(controls, function (el) {
      if (el.getAttribute("aria-label") || el.getAttribute("aria-labelledby")) return;
      if (el.closest("label")) return;
      if (el.id && document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id) + '"]')) return;
      var type = (el.getAttribute("type") || "").toLowerCase();
      if (type === "submit" || type === "button") {
        if (!el.value) el.value = "Submit";
        if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", el.value || "Submit");
        return;
      }
      el.setAttribute("aria-label", nameFor(el));
    });
  }

  /* --------------------------------------------------------------------------- */
  ready(function () {
    watchHidden(document.getElementById("SiteHeader"));
    enhanceAccordions();
    enhanceCarousels(0);
    watchCarousels();
    altFallback();
    labelFallback();
    // Re-run after load (+ a short delay) for late Flickity inits / injected imgs/controls.
    window.addEventListener("load", function () { enhanceCarousels(0); watchCarousels(); altFallback(); labelFallback(); });
    setTimeout(function () { altFallback(); labelFallback(); }, 2500);

    // App widgets (Yotpo reviews, Globo personalization) inject imgs/controls well
    // after load. Watch the body and re-apply the alt/label fallbacks (debounced) so
    // late nodes never announce as filename / unlabeled.
    var t;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { altFallback(); labelFallback(); }, 400);
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
